import LzdUtil from '@/utils/lzd-util';
import type { Column, Filters } from 'ele-admin-plus/es/ele-pro-table/types';
import type { CSSProperties, Ref } from 'vue';

type LzdI18nLabel = {
  /** 中文，默认 */
  cn: string;
  /** 英文 */
  en?: string;
  /** 繁体 */
  tw?: string;
};

/** 日期类型 */
export type LzdDateType =
  | 'year'
  | 'years'
  | 'month'
  | 'months'
  | 'date'
  | 'dates'
  | 'datetime'
  | 'week'
  | 'datetimerange'
  | 'daterange'
  | 'monthrange'
  | 'yearrange';

/**
 * Diff 差异结构定义
 */
export interface LzdDiffPart {
  text: string;
  type: 'add' | 'del' | 'normal';
}

/** 表单项配置 */
export interface LzdFormItem {
  /** 标识 */
  kid?: string;
  /** 总样式 */
  style?: CSSProperties;
  /** 类样式 */
  class?: string;
  /* 标签样式 */
  labelStyle?: CSSProperties;
  /** 值样式 */
  valueStyle?: CSSProperties;

  /** 表单段名 */
  prop?: string;
  /** 填充字段， 如 college */
  fillProp?: string;

  /** 显示文本 */
  label?: string;
  /** 显示模板 */
  labelFormat?: string;
  /** 值 */
  value?: any;
  /** 值模板 */
  valueFormat?: string;
  /** 值字符数 */
  valueChars?: number;
  /** 数据渲染时存储为模板预置值 */
  valuePreset?: any;
  /** 值必须做出修改，不能和模板值一样 */
  valueMustChange?: boolean;

  /** @description 中文，或者 {cn:'', en:'', tw:''} */
  i18nLabel?: LzdI18nLabel;
  /** 标签在上面显示，默认为 left */
  labelPosition?: 'left' | 'top' | 'right';
  /** 占位符 */
  placeholder?: string;
  /** 只读时占位符 */
  placeholderByReadonly?: string;
  /** 表单验证失败 */
  isError?: boolean;
  /** 仅显示标签，主要用于文本占位，不渲染表单组件 */
  isOnlyLabel?: boolean;
  /** 搜索 */
  isSearch?: boolean;
  /** 编辑 */
  isEdit?: boolean;
  /** 数字 */
  isNumber?: boolean;
  /** 只读 */
  isReadonly?: boolean;
  /** 多行输入框 */
  isRows?: boolean;
  /** 开关 */
  isSwitch?: boolean;
  /** 下拉框 */
  isSelect?: boolean;
  /** 复选框 */
  isCheckbox?: boolean;
  /** 单选 */
  isRadio?: boolean;
  /** 自动补全输入框 */
  isAutocomplete?: boolean;
  /** 签名 */
  isSignature?: boolean;
  /** 签章（盖章） */
  isSeal?: boolean;
  /** 标记是否用于审核 */
  isAudit?: boolean;
  /** 加载组织: orgType */
  loadOrg?: string;
  /** 加载字典： dictCode */
  loadDict?: string;
  /** 加载学生 */
  loadUser?: string;
  /** 是否多选，下拉的控制 */
  isMultiple?: boolean;
  /** 复选框，单选框、下拉框 */
  items?: KV[] | Ref<KV[]> | string;
  itemsFn?: () => KV[];
  min?: string | number;
  max?: string | number;

  /** 指定的 prop 有值才能编辑 */
  editByHavePropValue?: string;
  /** 指定的 prop 无值才能编辑 */
  editByHavePropNotValue?: string;

  /** 是否必填 */
  isRequired?: boolean;
  /** 日期选择器 */
  isDate?: boolean;
  /** 日期格式 'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY年MM月DD日' | 'YYYY-MM-DD HH:mm:ss' | 'YYYY/MM/DD HH:mm:ss' | 'YYYY年MM月DD日 HH时mm分ss秒' */
  dateFormat?: string;
  /** 日期类型 */
  dateType?: LzdDateType;
  /** 范围 */
  dateRange?: string;
  /** 值范围：week本周、month本月、nowBefore现在之前、nowAtfer现在之后 */
  dateValueRange?: 'week' | 'month' | 'nowBefore' | 'nowAtfer';
  /** 填充当前时间 */
  dateFill?: boolean;
  /** 条件类型 */
  dateDiffType?: 'lt' | 'le' | 'gt' | 'gt';
  /** 条件值 */
  dateDiffProp?: string;

  /** 富文本编辑器 */
  isEditor?: boolean;
  /** 富文本编辑器内容格式 */
  editorFormat?: 'text' | 'json' | 'html';
  /** 富文本编辑器类型 input:单行纯文本输入，textarea:多行纯文本输入， number:数字输入框 richtext: 富文本 */
  editorType?: 'input' | 'textarea' | 'number' | 'richtext';
  /** 在线Office */
  isOffice?: boolean;
  /** 是否上传文件 */
  isUpload?: boolean;
  /** 文件名称 */
  fileName?: string;
  /** 文件大小限制 */
  fileMaxSize?: number;
  /** 允许的文件后缀名：.doc,.docx,.ppt,.pptx */
  fileAccept?: string;
  /** 文件上传配置 */
  fileConfigPk?: number;

  /** 是表格 */
  isTable?: boolean;
  /** 字体 */
  fontName?: string;
  /** 字号 */
  fontSize?: string;
  /** 输入框字符限制 */
  maxLength?: number;
  /** 可以编辑的角色 */
  roles?: string[];
  /** 哪些角色不显示 */
  hideRoles?: string[];
  /** 只读角色 */
  readonlyRoles?: string[];
  /** AI提示词 */
  aiPromptWords?: string;
  /** 编辑提示词 */
  editPromptWords?: string;

  /** 是否开启评语分析功能 */
  evaluationEnabled?: boolean;
  /** 评语分析模版ID */
  evaluationTemplateId?: string;
  /** 评语分析模版名称 */
  evaluationTemplateName?: string;

  /** 是否开启监控告警功能 */
  monitorEnabled?: boolean;
  /** 监控告警模版ID */
  monitorTemplateId?: string;
  /** 监控告警模版名称 */
  monitorTemplateName?: string;

  /** 是否存在差异 */
  _isDiff?: boolean;

  /** 差异类型：当前版本(左) 还是 历史版本(右) */
  _diffType?: 'current' | 'history';

  /** 文本/富文本的字符级差异数组 */
  _diffArr?: LzdDiffPart[];

  /** 差异样式类名 (如 is-current-diff) */
  _diffClass?: string;

  /** 历史版本的值 (用于选择框对比) */
  _historyValue?: any;

  /** 最新版本的值 (用于选择框对比) */
  _latestValue?: any;
}

/** 数据表格列 */
export interface LzdColumn extends LzdFormItem, Partial<Column> {
  /** 导出 */
  isExport?: boolean;
}

/** 数据表格列集合 */
export type LzdColumns = LzdColumn[];

/** 表单图片 */
export interface LzdFormImage extends LzdFormItem {
  /** 图片标记 */
  isProImage: boolean;
}

/** 分页符 */
export interface LzdFormPageBreak extends LzdFormItem {
  /** 分页符 */
  isPageBreak: boolean;
}

/** 页面设置 */
export interface LzdFormPageSetup extends LzdFormItem {
  /** 页面设置 */
  isPageSetup: boolean;
  /** 页边距 上 */
  marginTop?: string;
  /** 页边距 下 */
  marginBottom?: string;
  /** 页边距 左 */
  marginLeft?: string;
  /** 页边距 右 */
  marginRight?: string;
}

/** 表单文本 */
export interface LzdFormText extends LzdFormItem {
  /** 文本标记 */
  isProText: boolean;
}

/** 表单段落 */
export interface LzdFormParagraph {
  kid?: string;
  /** 值 */
  value: (
    | LzdFormPageSetup
    | LzdFormPageBreak
    | LzdFormText
    | LzdFormImage
    | LzdFormItem
  )[];
  /** 样式 */
  style?: CSSProperties;
  /** 类样式 */
  class?: string;
  /** 段落标记 */
  isProParagraph: boolean;
}

/** 表单表格 */
export interface LzdFormTable {
  kid?: string;
  value: {
    /** 表头 */
    columns?: LzdFormTableRow[];
    /** 允许添加和删除行 */
    enabledUpdateRows?: boolean;
    /** 数据 */
    datas?: LzdFormTableRow[];
    /** 多个成绩占比汇总 */
    dataTotalItems?: {
      /** 汇总哪些字段 */
      dataTotalProps: string[];
      /** 汇总字段 */
      dataTotalName: string;
      /** 汇总值 */
      dataTotalValue: number;
      /** 权重 {} */
      dataTotalWeight?: Record<string, number>;
      /** 等级计算规则 [{k:'>=90',v:'优秀'},{k:'>=80',v:'良好'},{k:'>=70',v:'中等'},{k:'>=60',v:'合格'},{k:'<60',v:'不合格'}] */
      dataTotalLevelRules?: { k: string; v: string }[];
      /** 等级计算存储字段 */
      dataTotalLevelProp?: string;
    }[];
    /** 汇总哪些字段 */
    dataTotalProps?: string[];
    /** 汇总字段 */
    dataTotalName?: string;
    /** 汇总值 */
    dataTotalValue?: number;
    /** 权重 {} */
    dataTotalWeight?: Record<string, number>;
    /** 等级计算规则 [{k:'>=90',v:'优秀'},{k:'>=80',v:'良好'},{k:'>=70',v:'中等'},{k:'>=60',v:'合格'},{k:'<60',v:'不合格'}] */
    dataTotalLevelRules?: { k: string; v: string }[];
    /** 等级计算存储字段 */
    dataTotalLevelProp?: string;
  };
  /** 样式 */
  style?: CSSProperties;
  /** 类样式 */
  class?: string;
  /** 表格标记 */
  isProTable: boolean;
}

/** 表单的表格行 */
export interface LzdFormTableRow {
  kid?: string;
  /** 允许添加和删除行 */
  enabledUpdateRow?: boolean;
  /** 列 */
  cells: LzdFormTableCell[];
  /** 行样式 */
  rowStyle?: CSSProperties;
  /** 隐藏，不显示 */
  hide?: boolean;
}

/** 表单的表格列 */
export interface LzdFormTableCell extends LzdFormItem {
  /** 列样式 */
  cellStyle?: CSSProperties;

  /** 子元素 */
  children?: LzdFormItem[];

  /** 合并行 */
  rowspan?: number;
  /** 合并列 */
  colspan?: number;
  /** 隐藏，不显示 */
  hide?: boolean;
}

/** 表单数据 */
export type LzdFormProData = LzdFormParagraph | LzdFormTable | LzdFormItem;

export interface ROrg {
  /** 所属学院 */
  college?: string;
  /** 所属系所 */
  department?: string;
  /** 所属教研室 */
  teaching?: string;
  /** 所属专业 */
  major?: string;
  /** 所属届次（批次） */
  graduation?: string;
}

/**
 * 接口统一返回结果
 */
export interface R<T> {
  /** 状态码 */
  code: number;
  /** 状态信息（简体中文）  */
  message?: string;
  /** 状态信息（英文）  */
  messageEn?: string;
  /** 状态信息（繁体中文）  */
  messageTw?: string;
  /** 异常信息 */
  error?: string;
  /** 返回数据 */
  data?: T;
}

/**
 * 分页查询统一结果
 */
export interface RL<T> {
  /** 返回数据 */
  list: T[];
  /** 总数量 */
  count: number;
}

/**
 * 请求基本参数
 */
export interface RP {
  tenantId?: string;
  /**应用Id */
  appId?: string;
  /** 当前数据范围(学年) */
  dataScopeId?: number;
  /** 请求标识ID uuid */
  requestId?: string;
  /** 请求角色标识 */
  roleCode?: string;
  /** 角色名称 */
  roleName?: string;
  moduleId?: string;
  docTypeId?: string;
  /** 项目模板ID */
  projectTemplateId?: string;
  /** 项目ID */
  projectId?: string;
  /** 项目名称 */
  projectName?: string;
  /** 项目模板的任务模板ID */
  taskTemplateId?: string;
  /** 项目任务ID */
  taskId?: string;
  /** 任务所属组织 */
  taskOrg?: ROrg;
  /** 项目任务名称 */
  taskName?: string;
  /** 追踪ID */
  trackId?: string;
  /** 答辩追踪ID */
  trackDpId?: string;
  /** 专家追踪ID */
  trackExId?: string;
  /** 文档追踪ID */
  trackDocId?: string;

  /** 其他ID */
  otherIds?: string[];
  /** 其他主键 */
  otherPks?: number[];

  /** 所属学院 */
  college?: string;
  /** 所属系所 */
  department?: string;
  /** 所属教研室 */
  teaching?: string;
  /** 所属专业 */
  major?: string;
  /** 所属届次（批次） */
  graduation?: string;

  /** 主键 */
  pks?: number[];
  /** 更新参数 */
  update?: Array<RParam>;
  /** 更新排序编号专用参数，{主键：排序号} */
  updateSort?: Record<number, number>;
  /** 查询条件 */
  where?: Array<RParam>;
  /** 是否分页 */
  paged?: boolean;
  /** 第几页 */
  page?: number;
  /** 每页多少条 */
  limit?: number;
  /** 排序字段 */
  sort?: string;
  /** 排序方式, asc升序, desc降序 */
  order?: string;
  /** 设置请求需要返回的字段，减少数据IO传输 */
  fields?: string[];
  /** 数据导出请求 */
  export?: boolean;
  /** 异步导出 */
  exportAsync?: boolean;
  /** 数据导出范围 */
  exportRange?: EXPORT_RANGE;
  /** 导出文件类型： <br>
   * excel: 列表导出 <br>
   * excelSummary: 统计导出 <br>
   * word: 生成word <br>
   * word2pdf: 生成pdf <br>
   * file: fileUrl附件 <br>
   * file2pdf: fileUrl附件转pdf <br>
   * fileMerge: 文件合并
   * pdf: filePdf附件 <br>
   * wordFileUrl: 自定义和上传fileUrl附件word格式 <br>
   * wordFileUrl2Pdf: 自定义和上传fileUrl附件转pdf格式 <br>
   * wordPdfUrl: 自定义和上传PdfUrl附件pdf格式 <br>
   * wordOther: 其他格式，输出word格式<br>
   * wordOtherPdf: 其他格式，输出pdf格式<br>
   * */
  exportFileType?: EXPORT_FILE_TYPE;

  /** 导出文件名 */
  exportFileName?: string;

  /**
   * 数据导出文件命名格式
   * ${studentId}_${studentName}_${thesisTitle}_${thesisType}
   */
  exportFileNameFormat?: string;

  /**
   * 文件导出 按学院/专业/文件名 进行分组 <br> <br>
   * 如：学院名称/专业名称/学号_学生姓名_导师姓名_论文题目_模块名称.xxx <br> <br>
   * 软件学院/人工智能/2024060666678_论小忧_论之道_深度学习在自然语言处理中的创新应用与性能优化研究_任务书.pdf
   */
  exportFileGrouping?: boolean;

  /** 单个还是批量：默认批量，也就是导出到ZIP */
  exportFileSingle?: boolean;

  /** 导出 excel 头部数据列 List&lt字段, 名称&gt; */
  exportExcelHeaders?: KV[];

  /** 导出的 excel 工作簿名称 */
  exportExcelSheetName?: string;

  /** 学生 */
  student?: Partial<TenantStudent>[];
  /** 老师 */
  teacher?: Partial<TenantTeacher>[];

  /** 其他参数 */
  others?: Record<string, unknown>;
}

/** 查询方式 */
export type RPARAM_QUERY_TYPE =
  | 'eq'
  | 'notEq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'in'
  | 'notIn'
  | 'isNull'
  | 'isNotNull'
  | 'like'
  | 'likeLeft'
  | 'likeRight'
  | 'notLike'
  | 'notLikeLeft'
  | 'notLikeRight'
  | 'between'
  | 'notBetween'
  | 'orFields'
  | 'orFieldsLike';

/** 值类型 */
export type RPARAM_CLAZZ =
  | 'long'
  | 'int'
  | 'date'
  | 'LocalDateTime'
  | 'LocalDate'
  | 'LocalTime'
  | 'timestamp'
  | 'boolean';

/** 数据范围: 1 当前选择，2 当前页，3 当前条件  */
export type EXPORT_RANGE = 1 | 2 | 3;

/** 文件类型：excel、excelSummary、word、word2pdf、pdf、file、fileMerge */
export type EXPORT_FILE_TYPE =
  | 'excel'
  | 'excelSummary'
  | 'word'
  | 'word2pdf'
  | 'file'
  | 'file2pdf'
  | 'fileMerge'
  | 'pdf'
  | 'wordFileUrl'
  | 'wordFileUrl2Pdf'
  | 'wordPdfUrl'
  | 'wordOther'
  | 'wordOtherPdf';

export type buildRParamFunction = (k: string, v: any) => RParam | undefined;
export type T = string | number | boolean | undefined;
export type Enabled = 0 | 1;
export type Dsabled = Enabled;

/**
 * 查询更新基本参数
 */
export class RParam {
  /** JPA 表类属性 */
  name?: string;
  /** 数据库 表字段 */
  columnName?: string;
  /** JPA OR表类属性 */
  orName?: string;
  /** JPA OR表类属性 */
  orNames?: string[];
  /** 数据库 OR表字段 */
  orColumnName?: string;
  /** 比较字段 */
  compareToName?: string;
  /** 值 */
  val?: any;
  /**
   * 查询方式条件 */
  type?: RPARAM_QUERY_TYPE;
  /** 值类型 */
  clazz?: RPARAM_CLAZZ;
  /** 不跳过空内容，为空也要加入查询条件，强制加入条件查询 */
  forceWhere?: boolean;

  static build(
    name: string,
    value: any,
    type?: RPARAM_QUERY_TYPE,
    clazz?: RPARAM_CLAZZ
  ) {
    const rp = new RParam();
    rp.name = name;
    rp.val = value;
    rp.type = type;
    rp.clazz = clazz;
    return rp;
  }
  static buildOrName(
    name: string,
    orName: string,
    value: any,
    type: RPARAM_QUERY_TYPE,
    clazz?: RPARAM_CLAZZ
  ) {
    const rp = new RParam();
    rp.name = name;
    rp.orName = orName;
    rp.val = value;
    rp.type = type;
    rp.clazz = clazz;
    return rp;
  }
  /** = ? */
  static eq(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'eq', clazz);
  }
  /** != ? */
  static ne(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'notEq', clazz);
  }
  /** < ? */
  static lt(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'lt', clazz);
  }
  /** <= ? */
  static lte(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'lte', clazz);
  }
  /** &gt; ? */
  static gt(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'gt', clazz);
  }
  /** &gt;= ? */
  static gte(name: string, value: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'gte', clazz);
  }
  static in(name: string, value: Array<any>, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'in', clazz);
  }
  static notIn(name: string, value: Array<any>, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, value, 'notIn', clazz);
  }
  static between(name: string, startsVal: T, endsVal: T, clazz?: RPARAM_CLAZZ) {
    return RParam.build(name, [startsVal, endsVal], 'between', clazz);
  }
  static notBetween(
    name: string,
    startsVal: T,
    endsVal: T,
    clazz?: RPARAM_CLAZZ
  ) {
    return RParam.build(name, [startsVal, endsVal], 'notBetween', clazz);
  }
  static like(name: string, value: string) {
    return RParam.build(name, value, 'like');
  }
  static likeLeft(name: string, value: string) {
    return RParam.build(name, value, 'likeLeft');
  }
  static likeRight(name: string, value: string) {
    return RParam.build(name, value, 'likeRight');
  }
  static notLike(name: string, value: string) {
    return RParam.build(name, value, 'notLike');
  }
  static notLikeLeft(name: string, value: string) {
    return RParam.build(name, value, 'notLikeLeft');
  }
  static notLikeRight(name: string, value: string) {
    return RParam.build(name, value, 'notLikeRight');
  }
  /** and (fields[0] = :value or fields[1]=:value or fields[0]=:value ...) */
  static orFields(fields: string[], value: string) {
    if (!fields?.length) {
      return void 0;
    }
    const rp = new RParam();
    rp.name = fields.shift();
    rp.orNames = fields;
    rp.val = value;
    rp.type = 'orFields';
    rp.clazz = void 0;
    return rp;
  }
  /** and (fields[0] like :value or fields[1] like :value or fields[0] like :value ...) */
  static orFieldsLike(fields: string[], value: string) {
    if (!fields?.length) {
      return void 0;
    }
    const rp = new RParam();
    rp.name = fields.shift();
    rp.orNames = fields;
    rp.val = value;
    rp.type = 'orFieldsLike';
    rp.clazz = void 0;
    return rp;
  }
  static edit(name: string, value: T) {
    const rp = new RParam();
    rp.name = name;
    rp.val = value;
    return rp;
  }
  /** 字段修改
   * @param data 修改数据
   * @param fn 构建函数 (k, v) => true 表示修改
   */
  static buildEdit(data?: Record<string, any>, fn?: Function) {
    const updateParams: RParam[] = [];
    if (!data) {
      return updateParams;
    }
    const RParamFilter = [null, undefined, 'seq', 'version'];
    Object.keys(data).forEach(function (k: string) {
      const v = data[k] as any;
      if (!RParamFilter.includes(v) && (!fn ? true : fn(k, v))) {
        const v = data[k] as any;
        if (
          typeof v === 'string' ||
          typeof v === 'number' ||
          typeof v === 'boolean'
        ) {
          updateParams.push(RParam.build(k, v, void 0, void 0));
        } else if (Array.isArray(v) && v.length > 0) {
          updateParams.push(RParam.in(k, v));
        } else if (
          typeof v === 'object' &&
          v.value != null &&
          v.value != void 0
        ) {
          updateParams.push(RParam.build(k, v.value, v.type, v.clazz));
        }
      }
    });
    return updateParams;
  }
  /**
   * 构造条件查询
   * @param fn 不传或返回 undefined 时 eq 查询
   */
  static buildWhere(data?: Record<string, any>, fn?: buildRParamFunction) {
    const whereParams: RParam[] = [];
    if (!data) {
      return whereParams;
    }
    if (data.createdAtStart && data.createdAtEnd) {
      whereParams.push(
        RParam.between(
          'createdAt',
          data.createdAtStart as string,
          data.createdAtEnd as string,
          'date'
        )
      );
      delete data.createdAtStart;
      delete data.createdAtEnd;
    }

    Object.keys(data).forEach(function (k: string) {
      const v = data[k] as any;
      if (v === null || v === undefined || v === '') {
        return;
      }
      const result: RParam | undefined = fn ? fn(k, v) : void 0;
      if (result) {
        whereParams.push(result);
        return;
      }
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean'
      ) {
        whereParams.push(RParam.eq(k, v));
      } else if (Array.isArray(v) && v.length > 0) {
        whereParams.push(RParam.in(k, v));
      } else if (
        typeof v === 'object' &&
        v.value != null &&
        v.value != undefined
      ) {
        whereParams.push(
          RParam.buildOrName(k, v.orName, v.value, v.type, v.clazz)
        );
      }
    });

    return whereParams;
  }

  /**
   * 构造条件查询
   * @param likeFields 需要 like 的字段
   */
  static buildWhereLike(data: Record<string, any>, ...likeFields: string[]) {
    return this.buildWhere(data, (k: string, v: any) =>
      likeFields.includes(k) ? RParam.like(k, v) : void 0
    );
  }

  /**
   * 构造条件查询
   * @param data 查询参数
   * @param filters 筛选参数
   */
  static buildWhereFilters(data: Record<string, any>, filters: Filters) {
    return this.buildWhere({ ...data, ...filters });
  }
}

/** 键值对 */
export class KV {
  /** 键 key */
  k?: string;
  /** 值 value */
  v?: string | number;
  /** 说明 label */
  l?: string;
  /** 值类型 type */
  t?: string;
  /** 下标 index */
  i?: number;
  pk?: number;
  kid?: string;
  /** 原始值 */
  item?: any;
  constructor(
    k: string,
    v: any,
    l?: string,
    t?: string,
    i?: number,
    pk?: number,
    kid?: string,
    item?: any
  ) {
    this.k = k;
    this.v = v;
    this.l = l;
    this.t = t;
    this.i = i;
    this.pk = pk;
    this.kid = kid;
    this.item = item;
  }

  static kid(prop: PropEntity) {
    let currkid = prop.kid;
    if (currkid) {
      return currkid;
    }
    // 兼容旧数据
    else if (
      typeof prop.propValue === 'string' &&
      prop.propValue.startsWith('{')
    ) {
      currkid = LzdUtil.parseJson<Record<string, any>>(prop.propValue)?.kid;
    }
    return currkid ?? LzdUtil.fastSimpleUUID();
  }

  /** 构造KV */
  static build(k: string, v: any, l?: string, t?: string, i?: number) {
    return new KV(k, v, l, t, i);
  }

  /** 构造KV 集合 */
  static buildByProps(props: PropEntity[]) {
    const results: KV[] = [];
    if (!props) {
      return results;
    }
    props.forEach((prop) => {
      const item = KV.build(
        prop.propId,
        prop.propValue,
        prop.propName,
        prop.propType,
        prop.propIndex
      );
      item.kid = KV.kid(prop);
      results.push(item);
    });
    return results;
  }

  /**
   * 把KV集合转为 扩展参数集合
   * @param eachFn 参数回调函数
   */
  static toProps<P extends PropEntity>(
    items: KV[],
    eachFn?: (prop: P) => void
  ) {
    const props: P[] = [];
    items
      .sort((a, b) => (a.i ?? 0) - (b.i ?? 0))
      .forEach((v) => {
        const prop = {
          kid: v.kid ?? LzdUtil.fastSimpleUUID(),
          propId: v.k,
          propValue: v.v,
          propName: v.l,
          propType: v.t,
          propIndex: v.i
        } as P;
        if (eachFn) {
          eachFn(prop);
        }
        props.push(prop);
      });

    return props;
  }

  /** 获取扩展参数值 */
  static getPropValueByPropId(items: KV[], propId: string) {
    const index = items.findIndex((v) => v.k === propId);
    return index >= 0 ? items[index].v : null;
  }

  /** 获取扩展参数 */
  static getPropByPropId<P extends PropEntity>(items: KV[], propId: string) {
    const index = items.findIndex((v) => v.k === propId);
    if (index < 0) {
      return null;
    }
    const item = items[index];
    return {
      kid: item.kid ?? LzdUtil.fastSimpleUUID(),
      propId: item.k,
      propValue: item.v,
      propName: item.l,
      propType: item.t,
      propIndex: item.i
    } as P;
  }
}

/** 表类超基类 */
export interface XEntity {
  /** 主键 */
  seq?: number;
  /** 租户ID */
  tenantId?: string;
  /** 数据范围（学年） */
  dataScopeId?: number;
  /** 应用ID*/
  appId?: string;
}

/** 单例全局表基类（不分库、不分表、不分应用） */
export interface SingleEntity extends XEntity {}

/** 表类基类 */
export interface BaseEntity extends XEntity {
  /** 所属租户名称 */
  tenantName?: string;
  /** 创建人ID */
  createdById?: string;
  /** 创建人姓名 */
  createdByName?: string;
  /** 创建说明 */
  createdMsg?: string;
  /** 创建时间 */
  createdAt?: string;

  /** 更新人ID */
  updatedById?: string;
  /** 更新人姓名 */
  updatedByName?: string;
  /** 更新说明 */
  updatedMsg?: string;
  /** 更新时间 */
  updatedAt?: string;
  /** 备注信息 */
  comments?: string;
  /** 版本号 */
  version?: number;
}

/**
 * 删除标记 表类基类
 * */
export interface DeletedEntity extends BaseEntity {
  /** 是否删除：1已删除 */
  deleted?: number;
  /** 删除人ID */
  deletedById?: string;
  /** 删除人姓名 */
  deletedByName?: string;
  /** 删除说明 */
  deletedMsg?: string;
  /** 删除时间 */
  deletedAt?: string;
}

/**
 * 删除标记 表类基类
 * */
export interface DisabledEntity extends BaseEntity {
  /** 是否禁用：1已禁用 */
  disabled: 0 | 1;
  /** 禁用人ID */
  disabledById?: string;
  /** 禁用人姓名 */
  disabledByName?: string;
  /** 禁用说明 */
  disabledMsg?: string;
  /** 禁用时间 */
  disabledAt?: string;
}

/**
 * 禁用和删除标记 表类基类
 * */
export interface DisabledDeletedEntity extends DisabledEntity, DeletedEntity {}

/**
 * 扩展参数 表类基类
 * */
export interface PropEntity extends XEntity {
  kid?: string;
  /** 扩展字段标识 */
  propId: string;
  /** 扩展字段名称 */
  propName?: string;
  /** 扩展字段值 */
  propValue?: string;
  /** 值类型 */
  propType?: string;
  /** 第几个参数 */
  propIndex?: number;
}

/**
 * 文档评审记录 表类基类
 * */
export interface ReviewRecordEntity extends XEntity {
  /** 学生学号 */
  userid?: string;
  /** 所属专业（二级学科） */
  major?: string;
  /** 第几次提交 */
  submitLen?: number;
  /** 评审编号：提交一次生成一个新的编号 */
  revRefId?: string;
  /** 评审对象标识 */
  revTargetSeq?: number;
  /** 评审人评审顺序 */
  revSortNumber?: number;
  /** 评审人角色ID */
  revRole?: string;
  /** 评审人角色名称 */
  revRoleName?: string;
  /** 评审状态：0 未审核 1通过 2驳回 */
  revStatus?: number;
  /** 评审人ID */
  revId?: string;
  /** 评审人姓名 */
  revName?: string;
  /** 评审人职称 */
  revJob?: string;
  /** 评审人意见 */
  revOpinion?: string;
  /** 评审人附件 */
  revFile?: string;
  /** 评审人电子签名 */
  revSign?: string;
  /** 评审人评审日期（字符串） */
  revAtStr?: string;
  /** 评审人评审日期 */
  revAt?: string;
  /** 创建时间 */
  createdAt?: string;

  /** 等待审核 */
  waiting?: boolean;
  /** 等待审核 */
  prevWaiting?: boolean;
  /** 终止审核 */
  terminated?: boolean;
  /** 状态颜色 */
  statusColor?: string;
}

export interface ReviewRecordResult {
  at: string;
  status: number;
  records: ReviewRecordEntity[];
}

/**
 * 学生信息
 */
export interface TenantStudent extends BaseEntity {
  /** 密码 */
  password?: string;
  /** 学生ID */
  userid?: string;
  /** 学生姓名 */
  username?: string;
  /** 考生号 */
  kid?: string;
  /** 学生职称 */
  job?: string;
  /** 学生性别：0女、1男、2未知 */
  sex?: number;
  /** 学生年龄 */
  age?: number;
  /** 学生电子签名 */
  sign?: string;
  /** 手机号 */
  phone?: string;
  /** 联系邮箱 */
  email?: string;
  /** 学生类别：0普通学生，1留学生，2保送外校生 */
  category?: number;
  /** 个人简历 */
  resumeUrl?: string;
  /** 所属国籍 */
  nationality?: string;
  /** 所属民族 */
  ethnicity?: string;
  /**学历 */
  education?: string;

  /** 实习岗位名称 */
  workName?: string;
  /** 实习岗位类型 */
  workCategory?: string;
  /** 实习岗位职责 */
  workDuty?: string;
  /** 实习时间 */
  workTimeAtStr?: string;

  /** 是否校外完成毕设：0校内，1校外 */
  outside?: number;
  /** 毕设所在单位：校内填写大学名称、校外填写企业名称 */
  enterprise?: string;
  /** 单位社会统一信用代码 */
  enterpriseCode?: string;
  /** 单位所在地区：四川省/成都市/武侯区 */
  enterpriseDistrict?: string;
  /** 单位详细地址 */
  enterpriseAddress?: string;
  /** 单位联系人 */
  enterpriseContacts?: string;
  /** 单位联系邮箱 */
  enterpriseEmail?: string;
  /** 单位联系手机 */
  enterprisePhone?: string;
  /** 单位联系电话（座机） */
  enterpriseLandline?: string;
  /** 是否自行联系实习单位：1是 */
  enterpriseSelf?: number;

  /** 学位类型：博士、硕士、学士、专科 */
  degree?: string;
  /** 论文类型：毕业论文、毕业论文、保密论文 */
  type?: string;
  /** 论文题目 */
  title?: string;
  /** 论文英文题目 */
  titleEn?: string;
  /** 论文题目类型（研究方法或视角：实证研究、理论研究、文献综述、案例研究、比较研究、政策分析） */
  titleType?: string;
  /** 论文题目类别（学术领域或学科分类：人工智能、软件工程、网络安全、数据结构与算法、计算机网络） */
  titleCategory?: string;
  /** 论文题目来源 */
  titleSource?: string;
  /** 论文研究方向 */
  research?: string;
  /** 关键词：提取题目关键词, 用分号隔开 */
  keywords?: string;
  /** 英文关键词：提取题目关键词, 用分号隔开 */
  keywordsEn?: string;
  /** 论文撰写语种 */
  languages?: string;
  /** 毕设输出类型（成果形式） */
  result?: string;
  /** 是否公布：1公布 */
  titlePublished?: number;

  /** 所属学校：sys_university.seq */
  universitySeq?: number;
  /** 所属学校学校中文名称: sys_university.cname */
  universityName?: string;
  /** 所属学校学校代码 */
  universityCode?: string;

  /** 所属学院 */
  college?: string;
  /** 所属系所 */
  department?: string;
  /** 所属教研室 */
  teaching?: string;
  /** 所属专业（二级学科） */
  major?: string;
  /** 主修专业方向 */
  majorDir?: string;
  /** 是否本专业第一届毕业生：0否，1是 */
  majorFg?: number;
  /** 是否辅修：0主修、1辅修 */
  minored?: number;
  /** 辅修专业 */
  minor?: string;
  /** 辅修专业方向 */
  minorDir?: string;
  /** 所属年级 */
  grade?: string;
  /** 所属班级（行政班级） */
  adminclass?: string;
  /** 所属届次（批次） */
  graduation?: string;

  /** 学科门类名称 */
  disCategory?: string;
  /** 学科子门类名称（一级学科名称） */
  disSubcategory?: string;

  /** 指导教师ID（校内、第一导师） */
  advisorId?: string;
  /** 指导教师姓名 */
  advisorName?: string;
  /** 指导教师职称 */
  advisorJob?: string;
  /** 指导教师学历 */
  advisorEducation?: string;
  /** 指导教师性别：0女、1男、2未知 */
  advisorSex?: number;
  /** 指导教师年龄 */
  advisorAge?: number;
  /** 校外导师：0校内、1校外 */
  advisorOutside?: number;
  /** 指导教师所在单位：校外导师填写企业名称、校内填写学院或学校名称 */
  advisorEnterprise?: string;
  /** 指导教师电子签名 */
  advisorSign?: string;
  /** 手机号 */
  advisorPhone?: string;
  /** 邮箱 */
  advisorEmail?: string;

  /** 辅导教师ID（校外、企业、第二导师） */
  tutorId?: string;
  /** 辅导教师姓名 */
  tutorName?: string;
  /** 辅导教师职称 */
  tutorJob?: string;
  /** 辅导教师学历 */
  tutorEducation?: string;
  /** 辅导教师性别：0女、1男、2未知 */
  tutorSex?: number;
  /** 辅导教师年龄 */
  tutorAge?: number;
  /** 校外导师：0校内、1校外 */
  tutorOutside?: number;
  /** 辅导教师所在单位：校外导师填写企业名称、校内填写学院或学校名称 */
  tutorEnterprise?: string;
  /** 辅导教师电子签名 */
  tutorSign?: string;
  /** 手机号 */
  tutorPhone?: string;
  /** 邮箱 */
  tutorEmail?: string;
  /** 学生状态 */
  tags?: string;

  /** 使用项目模板ID */
  projectTemplateId?: string;
  /** 使用项目模板名称 */
  projectTemplateName?: string;
}

/**
 * 导师基类
 */
export interface AdvisorEntity extends BaseEntity {
  /** 密码 */
  password?: string;
  /** 学生ID */
  userid?: string;
  /** 学生姓名 */
  username?: string;
  /** 学生性别：0女、1男、2未知 */
  sex?: number;
  /** 学生年龄 */
  age?: number;
  /** 职称 */
  job?: string;
  /** 所属专业（可评专业1） */
  major?: string;
  /** 可评专业1方向(major 1 direction) */
  major1d?: string;
  /** 可评专业1语种(major 1 review languages) */
  major1rl?: string;

  /** 可评专业2 */
  major2?: string;
  /** 可评专业2方向(major 2 direction) */
  major2d?: string;
  /** 可评专业2语种(major 2 review languages) */
  major2rl?: string;

  /** 可评专业3 */
  major3?: string;
  /** 可评专业3方向(major 3 direction) */
  major3d?: string;
  /** 可评专业3语种(major 3 review languages) */
  major3rl?: string;

  /** 可评专业4 */
  major4?: string;
  /** 可评专业4方向(major 4 direction) */
  major4d?: string;
  /** 可评专业4语种(major 4 review languages) */
  major4rl?: string;

  /** 可评专业5 */
  major5?: string;
  /** 可评专业5方向(major 5 direction) */
  major5d?: string;
  /** 可评专业5语种(major 5 review languages) */
  major5rl?: string;

  /** 签名图片 */
  sign?: string;
  /** 头像图片 */
  avatar?: string;

  /** 联系邮箱 */
  email?: string;
  /** 联系电话 */
  phone?: string;
  /** 办公座机号 */
  landline?: string;

  /** 校外导师：0校内、1校外 */
  category?: number;
  /** 个人简历文件：pdf格式 */
  resumeUrl?: string;
  /** 所属国籍 */
  nationality?: string;
  /** 所属民族 */
  ethnicity?: string;

  /** 所属企业 */
  enterprise?: string;
  /**学位 */
  degree?: string;
  /**学历 */
  education?: string;

  /** 所属学校：sys_university.seq */
  universitySeq?: number;
  /** 所属学校学校中文名称: sys_university.cname */
  universityName?: string;
  /** 所属学校学校代码 */
  universityCode?: string;

  /** 所属学院 */
  college?: string;
  /** 所属系所 */
  department?: string;
  /** 所属教研室 */
  teaching?: string;

  /** 研究方向 */
  research?: string;
  /** 主讲课程及主要研究领域 */
  course?: string;
  /** 教师简介 */
  resume?: string;
  /** 和指导学生见面安排 */
  meetplan?: string;
  /** 办公地址 */
  officeaddr?: string;

  /** 银行卡号 */
  bankId?: string;
  /** 开户行 */
  bankName?: string;
  /** 聘请单位 */
  companyName?: string;
  /** 校内校外双导师：0双导师、1辅导教师 */
  tutor?: number;

  /** 教务处工号 */
  academicAffairsId?: string;
  /** 是否和学校签订外聘教师协议：1已签订、0未签订 */
  externalAgreement?: number;
}

/**
 * 教师
 */
export interface TenantTeacher extends AdvisorEntity {
  /** 最多留学生数(国外的) */
  foreignStuSize?: number;
  /** 最多外校学生数（保送）*/
  outsideStuSize?: number;
  /** 最少学生数 */
  minStuSize?: number;
  /** 合计最多学生数*/
  maxStuSize?: number;
}

/**
 * 专家
 */
export interface TenantExpert extends AdvisorEntity {}

/**
 * 题目表基类
 */
export interface TitleEntity extends DeletedEntity {
  /** 标识：学年-UUID */
  titleId: string;
  /** 题目 */
  title: string;
  /** 英文题目 */
  titleEn: string;
  /** 题目研究方向 */
  direction: string;
  /** 类型：毕业论文、毕业设计、涉密论文、无 */
  type: string;
  /** 类别：学校自定义 */
  category: string;
  /** 性质：学校自定义 */
  nature: string;
  /** 来源 */
  source: string;
  /** 是否新开 */
  now: 0 | 1;
  /** 选题方式：1学生自选、2导师指定 */
  choice: 0 | 1 | 2;

  /** 毕业设计起止时间 */
  startend: string;
  /** 毕设周数 */
  weeks: number;
  /** 难易程度 */
  difficulty: string;
  /** 工作量 */
  workload: string;
  /** 实习地点 */
  internship: string;
  /** 设计地点 */
  design: string;
  /** 学生人数 */
  students: number;

  /** 主要内容 */
  content: string;
  /** 指导思想 */
  ideology: string;

  /** 所属学院 */
  college: string;
  /** 所属系别 */
  department: string;
  /** 所属教研室 */
  teaching: string;
  /** 所属专业 */
  major: string;
  /** 所属届次 */
  graduation: string;
  /** 所属年级 */
  grade: string;
  /** 学年度 */
  annual: string;
  /** 可选专业：哪些专业的学生可以选择题目 */
  opts: string;

  /**  first teacher id */
  /** 第一指导教师ID、校内指导教师 （first teacher id） */
  ftId: string;
  /** 指导教师姓名 */
  ftName: string;
  /** 指导教师职称 */
  ftJob: string;
  /** 指导教师性别：0女、1男、2未知 */
  ftSex: 0 | 1 | 2;
  /** 指导教师年龄 */
  ftAge: number;
  /** 校内校外导师：0校内、1校外 */
  ftOutside: 0 | 1;
  /** 指导教师所在单位：校外导师填写企业名称、校内填写学院或学校名称 */
  ftEnterprise: string;
  /** 指导教师电子签名 */
  ftSign: string;

  /** second teacher id */
  /** 第二位教师ID、辅导教师、校外导师 （second teacher id） */
  stId: string;
  /** 指导教师姓名 */
  stName: string;
  /** 指导教师职称 */
  stJob: string;
  /** 指导教师性别：0女、1男、2未知 */
  stSex: 0 | 1 | 2;
  /** 指导教师年龄 */
  stAge: number;
  /** 校内校外导师：0校内、1校外 */
  stOutside: 0 | 1;
  /** 指导教师所在单位：校外导师填写企业名称、校内填写学院或学校名称 */
  stEnterprise: string;
  /** 指导教师电子签名 */
  stSign: string;

  /** 是否发布：1发布 */
  published: 0 | 1;
  /** 发布人ID */
  publishedById: string;
  /** 发布人姓名 */
  publishedByName: string;
  /** 发布时间：重复发布保持上次的时间 */
  publishedByAt: string;

  /** 是否分配：1是 */
  assigned: 0 | 1;
  /** 分配操作人 ID */
  assignedById: string;
  /** 分配操作人 姓名 */
  assignedByName: string;
  /** 分配说明 */
  assignedMsg: string;
  /** 分配日期 */
  assignedAt: string;
  /** 分配给谁 ID */
  assignedToId: string;
  /** 分配给谁 姓名 */
  assignedToName: string;
}

// ---------------------------------------
/**
 * 接口统一返回结果
 * @deprecated 请使用 R
 */
export interface ApiResult<T> extends R<T> {}

/**
 * 分页查询统一结果
 * @deprecated 请使用 RL
 */
export interface PageResult<T> extends RL<T> {}

/**
 * 分页查询基本参数
 * @deprecated 请使用 RP
 */
export interface PageParam extends RP {}

/** 全局角色名称映射 */
export const GLOBAL_ROLE_REF: Record<string, string> = {
  student: '学生',
  teacher: '指导教师',
  major: '专业负责人',
  majorm: '专业管理员',
  teaching: '教研室主任',
  teachingm: '教研管理员',
  department: '系主任',
  departmentsec: '系教学秘书',
  departmentm: '系管理员',
  college: '学院院长',
  collegem: '学院管理员',
  collegeted: '学院教学院长',
  collegetez: '学院教学主任',
  collegesec: '学院教学秘书',
  collegeins: '学院督察员',
  grade: '年级负责人',
  graduation: '届次负责人',
  adminclass: '班级负责人',
  expert: '评审专家',
  defensegl: '小组组长',
  defensegs: '小组秘书',
  defensegm: '小组成员'
};

/** 全局角色列表 */
export const GLOBAL_ROLES = Object.keys(GLOBAL_ROLE_REF).map((v) => ({
  v,
  l: GLOBAL_ROLE_REF[v]
}));
